import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Send, MoreVertical, Trash2, Share2, UserX, AlertTriangle, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import useWebSocket from '../hooks/useWebSocket';
import VerifiedBadge from '../components/VerifiedBadge';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ChatPage = () => {
  const { userId } = useParams();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [otherUser, setOtherUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [showMenu, setShowMenu] = useState(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [showContactShare, setShowContactShare] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportReason, setReportReason] = useState('');
  
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  
  const { isConnected, lastMessage, sendTypingIndicator, notifyNewMessage, notifyMessageDeleted } = useWebSocket(currentUserId);

  const returnUrl = location.state?.returnUrl || '/messages';
  const fromMessages = location.state?.from === 'messages';

  // Request notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Handle incoming WebSocket messages
  useEffect(() => {
    if (!lastMessage || !currentUserId) return;

    if (lastMessage.type === 'new_message') {
      // Only add message if it's from the OTHER user (not current user)
      if (lastMessage.message && lastMessage.message.senderId === userId) {
        setMessages(prev => [...prev, lastMessage.message]);
        scrollToBottom();
        
        // Show browser notification if not focused
        if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
          new Notification(`New message from ${otherUser?.firstName}`, {
            body: lastMessage.message.content,
            icon: otherUser?.image
          });
        }
      }
    } else if (lastMessage.type === 'typing' && lastMessage.userId === userId) {
      setIsTyping(lastMessage.isTyping);
    } else if (lastMessage.type === 'message_deleted' && lastMessage.messageId) {
      setMessages(prev => prev.map(msg => 
        msg._id === lastMessage.messageId 
          ? { ...msg, deletedBy: [...(msg.deletedBy || []), currentUserId] }
          : msg
      ));
    }
  }, [lastMessage, userId, otherUser, currentUserId]);

  useEffect(() => {
    // Wait for auth to fully load before making any decisions
    if (authLoading) {
      return;
    }
    
    // Don't fetch if not authenticated (redirect handled in render)
    if (!isAuthenticated) {
      return;
    }
    
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/user/me`, {
          headers: { 'Authorization': `Bearer ${token}` },
          withCredentials: true
        });
        setCurrentUserId(response.data.user.id);
      } catch (error) {
        console.error('Failed to fetch current user:', error);
      }
    };
    
    fetchCurrentUser();
    fetchMessages();
    checkBlockStatus();
  }, [authLoading, isAuthenticated, userId]);

  useEffect(() => {
    if (!loading && messages.length > 0) {
      scrollToBottom();
    }
  }, [loading]);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/messages/conversation/${userId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          withCredentials: true
        }
      );
      setMessages(response.data.messages || []);
      setOtherUser(response.data.otherUser);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkBlockStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/messages/is-blocked/${userId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          withCredentials: true
        }
      );
      setIsBlocked(response.data.isBlocked);
    } catch (error) {
      console.error('Failed to check block status:', error);
    }
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
    }
  };

  const handleSendMessage = async (e, messageType = 'text') => {
    if (e) e.preventDefault();
    if (!newMessage.trim() && messageType === 'text') return;
    if (isBlocked) {
      toast({
        title: "Cannot send message",
        description: "You have blocked this user",
        variant: "destructive"
      });
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/messages/send`,
        {
          receiverId: userId,
          content: newMessage,
          messageType
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );
      
      const sentMessage = {
        _id: response.data.messageId,
        senderId: currentUserId,
        receiverId: userId,
        content: newMessage,
        messageType,
        createdAt: new Date().toISOString(),
        isRead: false,
        deletedBy: []
      };
      
      setMessages(prev => [...prev, sentMessage]);
      setNewMessage('');
      
      // Notify via WebSocket
      notifyNewMessage(userId, sentMessage);
      
      scrollToBottom();
    } catch (error) {
      console.error('Failed to send message:', error);
      
      // Check if it's a premium required error
      if (error.response?.status === 403 && error.response?.data?.detail?.includes('Premium')) {
        toast({
          title: "Premium Required",
          description: "Upgrade to premium to send messages",
          variant: "destructive"
        });
        navigate('/pricing');
      } else {
        toast({
          title: "Error",
          description: "Failed to send message",
          variant: "destructive"
        });
      }
    } finally {
      setSending(false);
    }
  };

  const handleTyping = (e) => {
    setNewMessage(e.target.value);
    
    // Send typing indicator
    if (isConnected) {
      sendTypingIndicator(userId, true);
      
      // Clear previous timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Stop typing after 2 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        sendTypingIndicator(userId, false);
      }, 2000);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/api/messages/delete/${messageId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          withCredentials: true
        }
      );
      
      setMessages(prev => prev.map(msg => 
        msg._id === messageId 
          ? { ...msg, deletedBy: [...(msg.deletedBy || []), currentUserId] }
          : msg
      ));
      
      notifyMessageDeleted(userId, messageId);
      
      toast({
        title: "Message deleted",
        description: "Message has been deleted"
      });
    } catch (error) {
      console.error('Failed to delete message:', error);
      toast({
        title: "Error",
        description: "Failed to delete message",
        variant: "destructive"
      });
    }
    setShowMenu(null);
  };

  const handleShareContact = () => {
    setShowContactShare(true);
    setShowMenu(null);
  };

  const shareContactInfo = (type) => {
    let contactMessage = '';
    if (type === 'phone') {
      contactMessage = `📞 My phone: ${user?.phone || 'Not provided'}`;
    } else if (type === 'email') {
      contactMessage = `📧 My email: ${user?.email || 'Not provided'}`;
    }
    
    setNewMessage(contactMessage);
    setShowContactShare(false);
    // Will be sent when user clicks send
  };

  const handleBlockUser = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/messages/block-user`,
        { userId, reason: 'Blocked from chat' },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );
      
      setIsBlocked(true);
      setShowBlockConfirm(false);
      
      toast({
        title: "User blocked",
        description: "You have blocked this user"
      });
    } catch (error) {
      console.error('Failed to block user:', error);
      toast({
        title: "Error",
        description: "Failed to block user",
        variant: "destructive"
      });
    }
  };

  const handleUnblockUser = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/messages/unblock-user`,
        { userId },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );
      
      setIsBlocked(false);
      
      toast({
        title: "User unblocked",
        description: "You have unblocked this user"
      });
    } catch (error) {
      console.error('Failed to unblock user:', error);
      toast({
        title: "Error",
        description: "Failed to unblock user",
        variant: "destructive"
      });
    }
  };

  const handleReportUser = async () => {
    if (!reportReason.trim()) {
      toast({
        title: "Error",
        description: "Please provide a reason for reporting",
        variant: "destructive"
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/messages/report-user`,
        { userId, reason: reportReason },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );
      
      setShowReportDialog(false);
      setReportReason('');
      
      toast({
        title: "User reported",
        description: "Thank you for reporting. We will review this."
      });
    } catch (error) {
      console.error('Failed to report user:', error);
      toast({
        title: "Error",
        description: "Failed to report user",
        variant: "destructive"
      });
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      const date = new Date(timestamp);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.error('Invalid timestamp:', timestamp);
        return '';
      }
      
      const now = new Date();
      const diff = now - date;
      const isToday = date.toDateString() === now.toDateString();
      const isYesterday = new Date(now - 86400000).toDateString() === date.toDateString();
      
      // Use user's local timezone
      const timeString = date.toLocaleTimeString(undefined, { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
      
      if (isToday) {
        return timeString;
      } else if (isYesterday) {
        return `Yesterday ${timeString}`;
      } else if (diff < 604800000) { // Less than 7 days
        const dayName = date.toLocaleDateString(undefined, { weekday: 'short' });
        return `${dayName} ${timeString}`;
      } else {
        return date.toLocaleDateString(undefined, { 
          month: 'short', 
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    } catch (error) {
      console.error('Error formatting time:', error, timestamp);
      return '';
    }
  };

  // ========================================
  // ALL REDIRECT LOGIC IS IN RENDER (declarative)
  // ========================================

  // 1️⃣ Wait for auth to load
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mint-500"></div>
      </div>
    );
  }

  // 2️⃣ Not authenticated → redirect to login (declarative)
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 3️⃣ Show loading while fetching messages
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mint-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10 shadow-sm">
        <div className="w-full px-2 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(returnUrl, { 
                  state: { refresh: true },
                  replace: false 
                })}
                className="flex items-center gap-2 hover:bg-gray-100"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">Back</span>
              </Button>
              {otherUser && (
                <Link to={`/profile/${otherUser.id}`} className="flex items-center gap-3">
                  <img
                    src={otherUser.image}
                    alt={otherUser.firstName}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-semibold">
                        {otherUser.firstName} {otherUser.lastName}
                      </h2>
                      {(otherUser.isVerified || otherUser.govIdVerified) && (
                        <VerifiedBadge size="small" showText={false} />
                      )}
                    </div>
                    {isTyping && (
                      <p className="text-sm text-gray-500 italic">typing...</p>
                    )}
                  </div>
                </Link>
              )}
            </div>
            
            {/* Menu */}
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowMenu(!showMenu)}
              >
                <MoreVertical className="w-5 h-5" />
              </Button>
              
              {showMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white border rounded-lg shadow-lg z-20">
                  <button
                    onClick={handleShareContact}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <Share2 className="w-4 h-4" />
                    Share Contact Info
                  </button>
                  <button
                    onClick={() => {
                      setShowReportDialog(true);
                      setShowMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 text-orange-600"
                  >
                    <AlertTriangle className="w-4 h-4" />
                    Report User
                  </button>
                  {isBlocked ? (
                    <button
                      onClick={handleUnblockUser}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 text-green-600"
                    >
                      <UserX className="w-4 h-4" />
                      Unblock User
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        setShowBlockConfirm(true);
                        setShowMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 text-navy-500"
                    >
                      <UserX className="w-4 h-4" />
                      Block User
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto" style={{ minHeight: '400px' }}>
        <div className="w-full px-2 py-6">
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No messages yet. Start the conversation!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((message) => {
                const isOwnMessage = message.senderId === currentUserId;
                const isDeleted = message.deletedBy?.includes(currentUserId);
                
                if (isDeleted) return null;
                
                return (
                  <div
                    key={message._id}
                    className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} group`}
                  >
                    <div className="relative">
                      <div
                        className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                          isOwnMessage
                            ? 'bg-mint-500 text-white'
                            : 'bg-white text-gray-900 border'
                        }`}
                      >
                        <p className="break-words">{message.content}</p>
                        <div className="flex items-center justify-between gap-2 mt-1">
                          <p
                            className={`text-xs ${
                              isOwnMessage ? 'text-red-100' : 'text-gray-500'
                            }`}
                          >
                            {formatTime(message.createdAt)}
                          </p>
                          {isOwnMessage && message.isRead && (
                            <span className="text-xs text-red-100">✓✓</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Delete button (only for own messages) */}
                      {isOwnMessage && (
                        <button
                          onClick={() => handleDeleteMessage(message._id)}
                          className="absolute -right-8 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded"
                        >
                          <Trash2 className="w-4 h-4 text-gray-500" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </div>

      {/* Input */}
      <div className="bg-white border-t sticky bottom-0">
        <div className="w-full px-2 py-4">
          {isBlocked && (
            <div className="text-center text-navy-500 text-sm mb-2">
              You have blocked this user. <button onClick={handleUnblockUser} className="underline">Unblock</button>
            </div>
          )}
          <form onSubmit={handleSendMessage} className="flex gap-2">
            <Input
              type="text"
              placeholder={isBlocked ? "Unblock to send messages" : "Type a message..."}
              value={newMessage}
              onChange={handleTyping}
              className="flex-1"
              disabled={sending || isBlocked}
            />
            <Button
              type="submit"
              disabled={sending || !newMessage.trim() || isBlocked}
              className="bg-mint-500 hover:bg-mint-600"
            >
              <Send className="w-5 h-5" />
            </Button>
          </form>
        </div>
      </div>

      {/* Contact Share Modal */}
      {showContactShare && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Share Contact Information</h3>
            <div className="space-y-3">
              <button
                onClick={() => shareContactInfo('phone')}
                className="w-full flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50"
              >
                <Phone className="w-5 h-5 text-gray-600" />
                <span>Share Phone Number</span>
              </button>
              <button
                onClick={() => shareContactInfo('email')}
                className="w-full flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50"
              >
                <Mail className="w-5 h-5 text-gray-600" />
                <span>Share Email Address</span>
              </button>
            </div>
            <button
              onClick={() => setShowContactShare(false)}
              className="w-full mt-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Block Confirm Modal */}
      {showBlockConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Block User?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to block {otherUser?.firstName}? You won't be able to send or receive messages.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowBlockConfirm(false)}
                className="flex-1 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBlockUser}
                className="flex-1 py-2 bg-mint-500 text-white rounded-lg hover:bg-mint-600"
              >
                Block
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Dialog */}
      {showReportDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Report User</h3>
            <textarea
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              placeholder="Please describe the reason for reporting..."
              className="w-full border rounded-lg p-3 mb-4 min-h-[100px]"
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowReportDialog(false);
                  setReportReason('');
                }}
                className="flex-1 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReportUser}
                className="flex-1 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
              >
                Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPage;
